import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { format } from "date-fns";
import { GripVertical, LogOut, Mail, UserPlus, Users } from "lucide-react";
import { type PointerEvent, useState } from "react";
import { createPortal } from "react-dom";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { ClaimWithRelations } from "../../api/use-claim";
import { useExtractInNewFamily, useUpdateClaimFamilies } from "../../api/use-update-claim";
import { PassengerBirthdateActions } from "./passenger-birthdate-actions";
import { PassengerEmailActions } from "./passenger-email-actions";
import {
  groupPassengersByFamilies,
  isMinor,
  movePassengerToFamily,
  type PassengerRow,
  toFamiliesPayload,
} from "./passenger-grouping";

const NEW_FAMILY_DROP_ID = "new-family";
const FAMILY_DROP_PREFIX = "family:";

/** « Mr. Eric DUPONT » → « ED » — the civility title is not part of the person. */
function initialsOf(civility: string): string {
  return civility
    .replace(/^(Mr|Mde|Mme|Mlle)\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** `claimId` enables the edit / remove actions on the address and birthdate — not on the drag preview. */
function PassengerContact({ row, claimId }: { row: PassengerRow; claimId?: string }) {
  const { birthDate, contact } = row.passenger;
  const minor = Boolean(birthDate && isMinor(birthDate));
  // Without an email the birth date is what tells passengers apart; a minor always shows it.
  const showBirthDate = Boolean(birthDate && (minor || !contact));

  return (
    <>
      <span
        className="grid size-8 shrink-0 place-items-center rounded-full bg-gray-100 text-xs font-medium text-gray-600"
        aria-hidden="true"
      >
        {initialsOf(row.passenger.civility)}
      </span>
      <div className="min-w-0 flex-1">
        {/* Without a birthdate (removed, or never known), the « + » next to the name adds it back. */}
        <p className="flex max-w-full items-center gap-1.5 text-sm font-medium text-gray-900">
          <span className="truncate">{row.passenger.civility}</span>
          {claimId && !birthDate ? <PassengerBirthdateActions claimId={claimId} row={row} /> : null}
        </p>
        {contact ? (
          <p className="inline-flex max-w-full items-center gap-1.5 text-xs text-gray-500">
            <Mail className="size-3 shrink-0 text-gray-400" aria-hidden="true" />
            <span className="truncate">{contact.toLowerCase()}</span>
            {claimId ? <PassengerEmailActions claimId={claimId} row={row} /> : null}
          </p>
        ) : null}
        {showBirthDate && birthDate ? (
          <p className="inline-flex max-w-full items-center gap-1.5 text-xs text-gray-700">
            <span>
              {format(new Date(birthDate), "dd/MM/yyyy")}
              {minor ? " · mineur" : ""}
            </span>
            {claimId ? <PassengerBirthdateActions claimId={claimId} row={row} /> : null}
          </p>
        ) : null}
      </div>
    </>
  );
}

/** Le bouton vit dans la ligne draggable : sans ça, le pointer-down démarre un drag au lieu d'un clic. */
function stopDrag(event: PointerEvent<HTMLElement>) {
  event.stopPropagation();
}

/** Un clic pour sortir un passager de sa famille (l'équivalent du dépôt sur la zone pointillée). */
function ExtractPassengerButton({
  row,
  onExtract,
}: {
  row: PassengerRow;
  onExtract: (row: PassengerRow) => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={`Sortir ${row.passenger.civility} de la famille`}
            onPointerDown={stopDrag}
            onClick={() => onExtract(row)}
            className="shrink-0 rounded-md p-1 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-100 hover:text-gray-700 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-blue-500"
          />
        }
      >
        <LogOut size={14} aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>Sortir de la famille</TooltipContent>
    </Tooltip>
  );
}

interface DraggablePassengerProps {
  claimId: string;
  row: PassengerRow;
  disabled: boolean;
  familySize: number;
  onExtract: (row: PassengerRow) => void;
}

function DraggablePassenger({ claimId, row, disabled, familySize, onExtract }: DraggablePassengerProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: row.userClaimId, disabled });

  return (
    <li
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "group flex items-center gap-3 px-3 py-2.5 select-none",
        !disabled && "cursor-grab touch-none transition-colors hover:bg-gray-50",
        isDragging && "opacity-40",
      )}
    >
      {!disabled ? (
        <GripVertical
          className="-ml-1 size-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500"
          aria-hidden="true"
        />
      ) : null}
      <PassengerContact row={row} claimId={claimId} />
      {!disabled && familySize > 1 ? <ExtractPassengerButton row={row} onExtract={onExtract} /> : null}
    </li>
  );
}

interface FamilyCardProps {
  claimId: string;
  family: PassengerRow[];
  editable: boolean;
  onExtract: (row: PassengerRow) => void;
}

function FamilyCard({ claimId, family, editable, onExtract }: FamilyCardProps) {
  const familyId = family[0].familyId;
  const { setNodeRef, isOver } = useDroppable({
    id: `${FAMILY_DROP_PREFIX}${familyId}`,
    disabled: !editable || !familyId,
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors",
        isOver && "border-blue-400 bg-blue-50/40 ring-2 ring-blue-200",
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/60 px-3 py-2">
        <p className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Users className="size-4 text-gray-400" aria-hidden="true" />
          Famille {family[0].familyName ?? "—"}
        </p>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
          {family.length} passager{family.length > 1 ? "s" : ""}
        </span>
      </div>
      <ul className="divide-y divide-gray-100">
        {family.map((row) => (
          <DraggablePassenger
            key={row.userClaimId}
            claimId={claimId}
            row={row}
            disabled={!editable}
            familySize={family.length}
            onExtract={onExtract}
          />
        ))}
      </ul>
    </div>
  );
}

function NewFamilyDropZone() {
  const { setNodeRef, isOver } = useDroppable({ id: NEW_FAMILY_DROP_ID });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex items-center justify-center gap-2 rounded-lg border-2 border-dashed px-3 py-3 text-sm transition-colors",
        isOver ? "border-blue-400 bg-blue-50 text-blue-700" : "border-gray-300 text-gray-500",
      )}
    >
      <UserPlus size={16} aria-hidden="true" />
      Déposer ici pour créer une nouvelle famille
    </div>
  );
}

interface FamilyBoardProps {
  claim: ClaimWithRelations;
  /** Drag-and-drop between families — only while the family structure can still change. */
  editable: boolean;
}

/**
 * Families of the claim with each passenger's contact email — one receivable contract is
 * generated per family, so jurists check both before generating. When editable, drop a
 * passenger on another family (`updateClaimFamilies`) or on the dashed zone (`extractInNewFamily`);
 * the per-passenger button extracts in one click.
 */
export function FamilyBoard({ claim, editable }: FamilyBoardProps) {
  const updateClaimFamilies = useUpdateClaimFamilies(claim.id);
  const extractInNewFamily = useExtractInNewFamily(claim.id);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const [activeRow, setActiveRow] = useState<PassengerRow | null>(null);
  // Grouping shown while the reassignment request is in flight — avoids a snap-back before refetch.
  const [optimisticFamilies, setOptimisticFamilies] = useState<PassengerRow[][] | null>(null);

  const families = optimisticFamilies ?? groupPassengersByFamilies(claim);
  const allRows = families.flat();
  const canDrag = editable && !updateClaimFamilies.isPending && !extractInNewFamily.isPending;
  const activeFamilySize = activeRow
    ? (families.find((family) => family[0].familyId === activeRow.familyId)?.length ?? 0)
    : 0;

  function extract(row: PassengerRow) {
    extractInNewFamily.mutate(row.userClaimId);
  }

  function handleDragStart({ active }: DragStartEvent) {
    setActiveRow(allRows.find((row) => row.userClaimId === active.id) ?? null);
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveRow(null);
    const row = allRows.find((candidate) => candidate.userClaimId === active.id);
    if (!row || !over) {
      return;
    }

    if (over.id === NEW_FAMILY_DROP_ID) {
      extract(row);
      return;
    }

    const targetFamilyId = String(over.id).slice(FAMILY_DROP_PREFIX.length);
    if (targetFamilyId === row.familyId) {
      return;
    }

    const next = movePassengerToFamily(families, row.userClaimId, targetFamilyId);
    setOptimisticFamilies(next);
    updateClaimFamilies.mutate(toFamiliesPayload(next), {
      onSettled: () => setOptimisticFamilies(null),
    });
  }

  if (!allRows.length) {
    return <p className="text-sm text-gray-500 italic">Aucun passager sur ce dossier.</p>;
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveRow(null)}
    >
      <div className="space-y-3">
        {families.map((family) => (
          <FamilyCard
            key={String(family[0].familyId)}
            claimId={claim.id}
            family={family}
            editable={canDrag}
            onExtract={extract}
          />
        ))}
        {activeRow && activeFamilySize > 1 ? <NewFamilyDropZone /> : null}
      </div>
      {/* Portaled: the claim pane slides with a CSS translate, which would make the overlay's
          `position: fixed` relative to the pane and its measured rect miss every family card. */}
      {createPortal(
        <DragOverlay>
          {activeRow ? (
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-lg">
              <GripVertical className="-ml-1 size-4 shrink-0 text-gray-500" aria-hidden="true" />
              <PassengerContact row={activeRow} />
            </div>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
